import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { Input } from '@components/text-input';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  BoardIcon,
  CalendarIcon,
  ChevronDownIcon,
  DocumentIcon,
  FileIcon,
  GanttIcon,
  ListIcon,
  WhiteBoardFileIcon,
  WhiteBoardIcon,
} from '@sketchnote/icons';
import { Button, Panel as Panel2, Tabs } from '@sketchnote/shared-components';
import { Emoji } from 'emoji-mart';
import { useRouter } from 'next/router';
import * as React from 'react';
import * as ReactDoMServer from 'react-dom/server';
import { AsyncPaginate } from 'react-select-async-paginate';
import { useTldrawApp } from 'sn-wb-tldraw';

import { fetchFiles } from '../../api/getFiles';

const iconList = {
  creation: <DocumentIcon wrapperClass={'mt-3'} fill="#878A92" />,
  whiteboard: <WhiteBoardIcon wrapperClass={'mt-4'} fill="#878A92" />,
  kanban: <BoardIcon className={'mt-3'} fill="#878A92" />,
  list: <ListIcon className={'mt-3'} fill="#878A92" />,
  gantt: <GanttIcon className={'mt-3'} fill="#878A92" />,
  calendar: <CalendarIcon className={'mt-4'} fill="#878A92" />,
  arrowDown: <ChevronDownIcon fill="#878A92" />,
};

interface EmojiProps {
  icon: any;
}
const EmojiIcon = ({ icon }: EmojiProps) => {
  return (
    <div className={' w-28 h-28 flex justify-center items-center'}>
      {icon?.id && icon?.type !== 'image' ? (
        <Emoji emoji={icon?.id} set="apple" size={28} />
      ) : icon?.id && icon?.type === 'image' ? (
        <img className=" object-center object-cover w-28 h-28" src={icon?.id} />
      ) : (
        <span className="bg-gray-100 rounded-sm m-auto items-center">
          <FileIcon className=" text-primary-300 rounded-sm block text-xl" />
        </span>
      )}
    </div>
  );
};

interface SelcectOptionProps {
  title: string;
  icon: string;
}

const SelcectOption: any = (props: SelcectOptionProps) => {
  return (
    <div className="flex gap-8 items-center">
      <EmojiIcon icon={props.icon} />

      <div>{props.title}</div>
    </div>
  );
};

export const FileUpload = React.memo(function FileUpload(): JSX.Element {
  const app = useTldrawApp();
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;

  const isSmallScreen = window.innerHeight < 800;

  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [value, onChange] = React.useState<any>(null);

  const createFile = async () => {
    app.createFile(
      `/${mode}/${orgId}/files/${value.selectedFile?._id}`,
      value.selectedFile.title === '' ? 'Untitled' : value.selectedFile.title,
      ReactDoMServer.renderToString(iconList[value.selectedFile.filetype]),
      value.selectedFile.shared,
      value.selectedFile.updatedBy.firstname,
      value.selectedFile.updatedAt,
      value.selectedFile.filetype === 'creation' ? 'document' : value.selectedFile.filetype,
      ReactDoMServer.renderToString(<EmojiIcon icon={value.selectedFile.icon} />)
    );
  };

  const loadOptions = React.useCallback(
    async (search, previousOptions) => {
      const response = await fetchFiles(currentPage);
      const hasMore = response.data.totalFiles > response.data.files.length + previousOptions;
      if (hasMore) {
        setCurrentPage(currentPage + 1);
      }
      return {
        options: response.data.files.map((file) => ({
          label: <SelcectOption title={file.title ? file.title : 'Untitled'} icon={file.icon} />,
          value: file._id,
          selectedFile: file,
        })),
        hasMore,
      };
    },
    [currentPage]
  );
  React.useEffect(() => {
    //removing overflow hidden or else submenu keeps getting cut off
    const fileMenus = document.getElementById('file-menu')?.querySelectorAll('.css-0');
    fileMenus?.forEach((menu) => menu.classList.remove('css-0'));
  }, []);

  return (
    <div id="file-menu">
      <Menu
        direction="ltr"
        isLazy
        placement="right"
        offset={[0, 15]}
        boundary="scrollParent"
        preventOverflow={true}
      >
        <div>
          <MenuButton
            className="w-36 h-36 smallScreen:w-23 smallScreen:h-23 flex justify-center items-center cursor-pointer"
            style={{
              border: '0.79px solid #E2E4E9',
              borderRadius: '3.17241px',
            }}
          >
            <WhiteBoardFileIcon wrapperClass={'transform smallScreen:scale-90 scale-135'} />
          </MenuButton>
          <p
            style={{
              fontSize: `${isSmallScreen ? '8px' : '12.48px'}`,
              textAlign: 'center',
              lineHeight: `${isSmallScreen ? '16px' : '24.96px'}`,
            }}
          >
            File
          </p>
        </div>

        <MenuList>
          <div className="flex flex-col py-10 px-12 gap-10 rounded-lg bg-white shadow-sm text-gray-999 min-w-284">
            <p className="font-medium text-sm m-0 text-gray-600">Add a File</p>
            <p className="font-small text-xs m-0 text-gray-600">Select a File</p>
            <AsyncPaginate
              loadOptions={loadOptions}
              value={value}
              onChange={onChange}
              isSearchable={false}
            />

            <MenuItem>
              <Button onClick={createFile} className="justify-center">
                Add
              </Button>
            </MenuItem>
          </div>
        </MenuList>
      </Menu>
    </div>
  );
});
